import {
	FormControl,
	FormGroup,
	FormBuilder,
	Validators
} from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { firstValueFrom, map } from 'rxjs';
import { term, FieldExpression, NodeExpression, toString as luceneToString } from 'lucene';

import { FORM_COIL, FORM_SLITTING_COIL, FORM_SHEET, FORM_BLANK, UNIT_TONS, UNIT_SHEET,
	UNIT_BLANKS, unitListForCoil, unitListForSheet, unitListForBlank } from '../models/fastlane-list';
import { BocClient } from '../../shared/models/boc-client-transac';
import { OFFER_SERVER_URL } from '../../config';
import { UserService } from '../../shared/services/user.service';
import { TranslationService } from '../../shared/services/translation.service';
import {
	BOC, FastLaneItem, FastLaneRequest,
	ValueAndDisplay
} from '../models/fastlane.model';
import {
	FastLaneItems, FieldOfPrototypes
} from '../models/fastlane-datacenter-config';
import { ErrorService } from '../../shared/services/error.service';
import { ConnectionService } from '../../shared/services/connection.service';
import { OrderBy } from '../../shared/utils/constant';
import { PaginatedResult } from '../../shared/models/paginated-result';
import { DataWithTranslation, TranslationType } from '../../shared/models/data-with-translations';
import { ProductCharacteristicsTranslationService } from
	'../../shared/services/product-characteristics-translation.services';

@Injectable({ providedIn: 'root' })
export class FastLaneService {
	filteredSoldtoShiptoList?: BOC[];
	filterShiptoList?: BocClient[];
	errMsg!: unknown;
	requestOrderObj!: FastLaneRequest;
	productFormGroup?: FormGroup;

	constructor(
		public fb: FormBuilder,
		private readonly http: HttpClient,
		private readonly userService: UserService,
		private readonly translationService: TranslationService,
		private readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService,

		private readonly snackBar: MatSnackBar,
		private readonly errorService: ErrorService,
		private readonly connectionService: ConnectionService
	) {}

	getBocs() {
		return this.userService.getSoldToRelationshipGroupsTransac('PFAST');
	}

	resetProdctFormGroup() {
		delete this.productFormGroup;
		this.errorService.resetErrorMsg();
	}

	showError(msg?: string) {
		this.snackBar.open(
			msg === undefined
				? this.translationService.getTR('quapp.error')
				: `${ this.translationService.getTR('quapp.error') } - ${ msg }`,
			undefined,
			{ duration: 5000 }
		);
	}

	uploadAttachment(file: File, hash: string) {
		const form = new FormData();
		form.append('file', file, file.name);
		return this.http.post(
			`${ OFFER_SERVER_URL }/v1/fastlaneRequests/fileupload/${ encodeURIComponent(hash) }`,
			form,
			{ responseType: 'text', reportProgress: true, observe: 'events' }
		);
	}

	deleteAttachment(id: string) {
		return this.http.delete(
			`${ OFFER_SERVER_URL }/v1/fastlaneRequests/attachments/${ id }`,
			{ responseType: 'text' }
		);
	}

	downloadTemplate() {
		const url = new URL(`${ OFFER_SERVER_URL }/v1/fastlanetemplates`);
		const params = url.searchParams;
		params.append('access_token', this.connectionService.getDownloadAccessToken());
		params.append('lang', this.connectionService.language);
		return url.toString();
	}

	async getTemplateFieldTranslation() {
		const url = new URL(`${ OFFER_SERVER_URL }/v1/fastlanetemplates:getTranslationsForCodes`);
		return firstValueFrom(this.http.get(
			url.toString()
		));
	}

	getFastLaneItems(
		fields: readonly FieldOfPrototypes[] | string[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof FastLaneItems>[] | OrderBy<keyof FastLaneItems>,
		onlyDistinct: boolean
	) {
		const url = new URL(`${ OFFER_SERVER_URL }/v1/fastlaneItems`);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));

		params.append('distinct', String(onlyDistinct));

		return this.http.get<PaginatedResult<FastLaneItems>>(url.toString());
	}

	getFastLaneItemsById(
		fastlaneId: string,
		translationType?: TranslationType
	) {
		const url = new URL(`${ OFFER_SERVER_URL }/v1/fastlaneItems/${ fastlaneId }`);
		const params = url.searchParams;
		if (translationType !== undefined) {
			params.append('translation', String(translationType));
		}
		return this.http.get<DataWithTranslation<FastLaneItems>>(url.toString()).pipe(map(fastLane => {
			this.productCharacteristicsTranslationService.add(fastLane.translations);
			return fastLane.data;
		}));
	}

	createFastlaneFormGroup(fastlaneItem: FastLaneItem) {
		const form = this.fb.group({
			name: new FormControl(fastlaneItem.name, { validators: [
				Validators.required,
				Validators.maxLength(80)
			] }),
			steelgrade: new FormControl(fastlaneItem.steelgrade, { validators: [
				Validators.required,
				Validators.maxLength(255)
			] }),
			thickness: new FormControl(fastlaneItem.thickness && fastlaneItem.thickness > 0
				? fastlaneItem.thickness : '', { validators: [
				Validators.required,
				Validators.max(999),
				Validators.min(0.1)
			] }),
			width: new FormControl(fastlaneItem.width && fastlaneItem.width > 0
				? fastlaneItem.width : '', { validators: [
				Validators.required,
				Validators.max(999_999_999),
				Validators.min(1)
			] }),
			length: new FormControl(fastlaneItem.length && fastlaneItem.length > 0
				? fastlaneItem.length : '', {
				validators: [
					Validators.max(999_999_999),
					Validators.min(1)
				]
			}),
			blankWeight: new FormControl(fastlaneItem.blankWeight && fastlaneItem.blankWeight > 0
				? fastlaneItem.blankWeight : '', { validators: [
				Validators.max(99_999_999),
				Validators.min(1)
			] }),
			quantity: new FormControl(fastlaneItem.quantity && fastlaneItem.quantity > 0
				? fastlaneItem.quantity : '', { validators: [
				Validators.required,
				Validators.max(99_999_999),
				Validators.min(1)
			] }),
			coating: new FormControl(fastlaneItem.coating, { validators: [
				Validators.required,
				Validators.maxLength(50)
			] }),
			oiling: new FormControl(fastlaneItem.oiling, { validators: [Validators.maxLength(50)] }),
			maxOutsideDiameter: new FormControl(fastlaneItem.maxOutsideDiameter, { validators: [
				Validators.min(1),
				Validators.max(999_999_999),
			] }),
			additionalItemInfo: new FormControl(fastlaneItem.additionalItemInfo),
			form: new FormControl(fastlaneItem.form, { validators: [Validators.required] }),
			quantityUnit: new FormControl(fastlaneItem.quantityUnit, { validators: [Validators.required] }),
			coilAxis: new FormControl(fastlaneItem.coilAxis, { validators: [] }),
			innerDiameter: new FormControl(fastlaneItem.innerDiameter, { validators: [] }),
			rddType: new FormControl(fastlaneItem.rddType, { validators: [] }),
			rddDate: new FormControl(fastlaneItem.rddDate),
			transportMode: new FormControl(fastlaneItem.transportMode, { validators: [] }),
			incoterms: new FormControl(fastlaneItem.incoterms, { validators: [Validators.required] }),
			packing: new FormControl(fastlaneItem.packing, { validators: [] }),
			plant: new FormControl(fastlaneItem.plant, { validators: [] }),
			totalWeight: new FormControl(fastlaneItem.totalWeight, { validators: [] }),
			sheetWeightKg: new FormControl(fastlaneItem.sheetWeightKg, { validators: [] }),
			fastLaneRequestItemAttachments: this.fb.array([]),
			shipToContact: new FormControl(fastlaneItem.shipToContact, { validators: [] }),
			shipTo: new FormControl(fastlaneItem.shipTo, { validators: [] }),
			shipToPerson: new FormControl(fastlaneItem.shipToPerson, { validators: [] }),
			shipToCustom: new FormControl(fastlaneItem.shipToCustom, { validators: [] }),
			attachmentErrorMessages: new FormControl('', { validators: [] })
		});
		return this.validateBasedOnFormValue(form);
	}

	createQuickSerachFilter(textSearch: string) {
		const search = `*${ term.escape(textSearch) }*`;
		const customerRequestNameField: FieldExpression = {
			field: 'customerRequestName',
			term: search,
			quoted: false
		};
		const prototypeNameField: FieldExpression = {
			field: 'prototypeName',
			term: search,
			quoted: false
		};
		const allNode: NodeExpression = {
			left: prototypeNameField,
			operator: 'OR',
			parenthesized: true,
			right: customerRequestNameField
		};
		return luceneToString(allNode);
	}

	private validateBasedOnFormValue(form: FormGroup) {
		const formControl = <FormControl> form.get('form');
		if (formControl.value) {
			const quantityUnitControl = <FormControl> form.get('quantityUnit');
			const widthControl = <FormControl> form.get('width');
			const lengthControl = <FormControl> form.get('length');
			const blankWeightControl = <FormControl> form.get('blankWeight');
			switch (formControl.value) {
			case FORM_COIL:
			case FORM_SLITTING_COIL:
				if (!quantityUnitControl.value
					|| !this.validUnit(unitListForCoil, String(quantityUnitControl.value))) {
					quantityUnitControl.setValue(UNIT_TONS);
				}
				widthControl.setValidators([
					Validators.required,
					Validators.min(1)
				]);
				lengthControl.setValidators([]);
				blankWeightControl.setValidators([]);
				break;
			case FORM_SHEET:
				if (!quantityUnitControl.value
					|| !this.validUnit(unitListForSheet, String(quantityUnitControl.value))) {
					quantityUnitControl.setValue(UNIT_SHEET);
				}
				widthControl.setValidators([
					Validators.required,
					Validators.min(1)
				]);
				lengthControl.setValidators([
					Validators.required,
					Validators.min(1)
				]);
				blankWeightControl.setValidators([]);
				break;
			case FORM_BLANK:
				if (!quantityUnitControl.value
					|| !this.validUnit(unitListForBlank, String(quantityUnitControl.value))) {
					quantityUnitControl.setValue(UNIT_BLANKS);
				}
				widthControl.setValidators([]);
				lengthControl.setValidators([]);
				blankWeightControl.setValidators([
					Validators.required,
					Validators.min(1)
				]);
				break;
			}
		}
		return form;
	}

	private validUnit(unitsAllowed: ValueAndDisplay[], unitValue: string) {
		for (const unit of unitsAllowed) {
			if (unitValue === unit.value) {
				return true;
			}
		}
		return false;
	}
}
